import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
    ArrowRight, Check, Sparkles, Users, Zap, Shield, Lock, LayoutGrid,
    Cable, Terminal as TerminalIcon, Star, Play,
} from "lucide-react";

import SectorsMegaMenu from "@/components/SectorsMegaMenu";
import ComparisonTable from "@/components/ComparisonTable";
import CharacterSection from "@/components/CharacterSection";
import BookDemoModal from "@/components/BookDemoModal";
import HeroReel from "@/components/HeroReel";
import IntegrationIcon from "@/components/IntegrationIcon";
import IntegrationLogo from "@/components/IntegrationLogo";
import TraitDots from "@/components/TraitDots";
import SocialProofBand from "@/components/SocialProofBand";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ============================================================
   Content — copy is largely fixed; workers/traits are live.
   ============================================================ */

// Sprint H — expanded set of stack logos (~24) rendered via IntegrationLogo
// (logo.dev with monochrome-glyph fallback). Domain is required for the
// logo.dev lookup — the icon is a graceful fallback.
const STACK_LOGOS = [
    { slug: "salesforce",         name: "Salesforce",       domain: "salesforce.com",    brand_color: "#00A1E0" },
    { slug: "hubspot",            name: "HubSpot",          domain: "hubspot.com",       brand_color: "#FF7A59" },
    { slug: "slack",              name: "Slack",            domain: "slack.com",         brand_color: "#4A154B" },
    { slug: "google-workspace",   name: "Google Workspace", domain: "workspace.google.com", brand_color: "#1FA463" },
    { slug: "microsoft-365",      name: "Microsoft 365",    domain: "microsoft.com",     brand_color: "#464EB8" },
    { slug: "notion",             name: "Notion",           domain: "notion.so",         brand_color: "#191919" },
    { slug: "zendesk",            name: "Zendesk",          domain: "zendesk.com",       brand_color: "#03363D" },
    { slug: "snowflake",          name: "Snowflake",        domain: "snowflake.com",     brand_color: "#29B5E8" },
    { slug: "github",             name: "GitHub",           domain: "github.com",        brand_color: "#181717" },
    { slug: "stripe",             name: "Stripe",           domain: "stripe.com",        brand_color: "#635BFF" },
    { slug: "zapier",             name: "Zapier",           domain: "zapier.com",        brand_color: "#FF4A00" },
    { slug: "airtable",           name: "Airtable",         domain: "airtable.com",      brand_color: "#18BFFF" },
    { slug: "figma",              name: "Figma",            domain: "figma.com",         brand_color: "#F24E1E" },
    { slug: "linear",             name: "Linear",           domain: "linear.app",        brand_color: "#5E6AD2" },
    { slug: "datadog",            name: "Datadog",          domain: "datadoghq.com",     brand_color: "#632CA6" },
    { slug: "docusign",           name: "DocuSign",         domain: "docusign.com",      brand_color: "#FFCC22" },
    { slug: "gong",               name: "Gong",             domain: "gong.io",           brand_color: "#8952E0" },
    { slug: "openai",             name: "OpenAI",           domain: "openai.com",        brand_color: "#412991" },
    { slug: "anthropic",          name: "Anthropic",        domain: "anthropic.com",     brand_color: "#D97706" },
    { slug: "quickbooks",         name: "QuickBooks",       domain: "quickbooks.intuit.com", brand_color: "#2CA01C" },
    { slug: "shopify",            name: "Shopify",          domain: "shopify.com",       brand_color: "#95BF47" },
    { slug: "greenhouse",         name: "Greenhouse",       domain: "greenhouse.io",     brand_color: "#3B7B45" },
    { slug: "intercom",           name: "Intercom",         domain: "intercom.com",      brand_color: "#5C51F3" },
    { slug: "okta",               name: "Okta",             domain: "okta.com",          brand_color: "#007DC1" },
];

const VALUE_PILLARS = [
    {
        icon: Users,
        title: "Activate specialists, not prompt windows.",
        body: "Each Workforce Member is a role, not a text box. They come with a persona, credentials, a way of working, and a set of tools they know how to use.",
    },
    {
        icon: Cable,
        title: "Living in your tools.",
        body: "Salesforce, Slack, Zendesk, Notion, DocuSign, and 600+ more. Your specialist doesn't ask you to leave your stack — they show up inside it.",
    },
    {
        icon: Shield,
        title: "Enterprise-grade governance.",
        body: "Multi-tenant workspace isolation, RBAC, audit trail, and read-only impersonation. SOC 2 in progress. Data segregation is a property, not a promise.",
    },
];

const SAMPLE_TASKS = {
    "ivan-legal":       "I've reviewed the Acme MSA. Three clauses are non-starters — unlimited liability, one-sided IP assignment, and vague termination-for-convenience. My redlines are attached. I'd walk away from this deal before agreeing to clause 12.",
    "sofia-data":       "The ARR forecast doesn't add up. Q3 shows 22% growth but net-new logos dropped 8% — someone's double-counting expansion. I found the leak: three renewals were logged as new business. Real growth: 14%. Details are attached, but this is not the story leadership thinks they're telling.",
    "marcus-recruiter": "I read 47 resumes for the Head of RevOps role today. Six are worth your time. Priya stands out — she's the only one who's built a RevOps function from scratch AND stayed 3+ years. I've already reached out to schedule. Full profiles attached.",
    "elena-exec":       "Your Thursday is overpacked. I moved the board prep to 7am (I know, sorry) so you have a real 45-minute lunch. Also — flowers ordered for Marion's retirement. She'll love that we remembered her orchid preference.",
    "kenji-support":    "Ticket #4821 is a frustrated customer, not a bug — the product works, but the onboarding doc misled them. I've drafted a response that owns our part, offers a 30-day extension, and a Loom walkthrough. I think we should also update the doc — 3 other tickets this week hint at the same issue.",
    "aria-content":     "The 'How We Cut Vendor Cycle Time' blog is written. But I wrote a better version — from the perspective of the general counsel whose weekend was saved by it. It's more honest, more human, and 3× more shareable. Both drafts attached. Choose your weapon.",
};

const FUNCTIONAL_SLUGS = ["ivan-legal", "sofia-data", "marcus-recruiter", "elena-exec", "kenji-support", "aria-content"];

const SOLUTIONS = [
    { name: "Legal",                pill: "Coming with Phase 4", body: "Contract review, matter management, ethical walls." },
    { name: "Government Contractors", pill: "Coming with Phase 5", body: "Compliance-first workflows for federal + state." },
    { name: "Healthcare",           pill: "Coming soon",         body: "HIPAA-ready workflows for provider ops." },
    { name: "Financial Services",   pill: "Coming soon",         body: "Reconciliation, close, and reporting flows." },
    { name: "Consulting",           pill: "Available now",       body: "Research, briefings, and deliverables at partner speed." },
    { name: "SaaS",                 pill: "Available now",       body: "GTM specialists that plug into your CRM & data warehouse." },
];

const STEPS = [
    { n: 1, title: "Sign up in 60 seconds.",   body: "No credit card. No sales call.", Icon: Zap },
    { n: 2, title: "Activate your first specialist.", body: "22 personas ready — pick the one that matches your need.", Icon: Users },
    { n: 3, title: "Connect their tools.",     body: "Mock-OAuth today, real integrations rolling out. Your specialist lives where the work already is.", Icon: Cable },
];

const SECURITY_BADGES = [
    { label: "SOC 2 · in progress",           color: "#0ea5e9" },
    { label: "ISO 27001 · in progress",        color: "#0ea5e9" },
    { label: "GDPR-ready",                     color: "#16a34a" },
    { label: "HIPAA-ready",                    color: "#16a34a" },
    { label: "Multi-tenant isolation",         color: "#111827" },
    { label: "Encrypted at rest & in transit", color: "#111827" },
];

const TESTIMONIALS = [
    { quote: "We replaced our contract-review backlog in 6 weeks.", who: "J.M., General Counsel", type: "Mid-market SaaS" },
    { quote: "Sofia found $2.1M in revenue leakage her first month.", who: "R.O., VP Finance",     type: "Series C fintech" },
    { quote: "Marcus screens more resumes than my entire recruiting team.", who: "P.K., Head of Talent", type: "Growth-stage healthcare" },
    { quote: "Our onboarding CSAT jumped 18 points once Kenji started triage.", who: "S.T., Head of Support", type: "Enterprise SaaS" },
];

const FAQS = [
    ["What is a Workforce Member?",                    "A role-shaped Workforce Member with a persona, credentials, tool access, and a system prompt tuned for its job — not a general-purpose prompt window."],
    ["Do I need to train it?",                     "No. Each specialist ships ready to work. You can layer your brand voice, playbooks, and policies on top."],
    ["Can it access my company data?",             "Only what you connect. Every workspace is tenancy-isolated; nothing leaks across companies."],
    ["What happens to my data?",                   "It stays in your tenant. We never train foundation models on your data."],
    ["How is this different from ChatGPT?",        "ChatGPT is a chat window. Nexus One is a team. Each worker has a role, tools, memory scoped to your company, and shows up where the work already happens."],
    ["What integrations are supported?",           "600+ across CRM, Support, Recruiting, Docs, Comms, Finance, Data, Marketing, Legal, Engineering, and more — with the full catalog on the /integrations page."],
    ["Is there a free trial?",                     "Every account starts on a 14-day free Professional plan trial."],
    ["Can I cancel anytime?",                      "Yes. Monthly billing, no lock-in."],
    ["Is my data isolated from other tenants?",    "Yes — company_id is enforced at every query. Two tenants literally cannot see each other's data."],
    ["Do you offer enterprise SSO?",               "SAML SSO is included on the Enterprise plan and above."],
];

/* ============================================================ */

export default function Landing() {
    const [workers, setWorkers] = useState([]);
    const [demoOpen, setDemoOpen] = useState(false);
    const [demoInterest, setDemoInterest] = useState("");

    useEffect(() => {
        axios.get(`${API_BASE}/workers/catalog`).then((r) => {
            setWorkers(r.data.workers || []);
        }).catch(() => {});
    }, []);

    const openDemo = (interest) => { setDemoInterest(interest || ""); setDemoOpen(true); };
    const functional = FUNCTIONAL_SLUGS
        .map((s) => workers.find((w) => w.slug === s))
        .filter(Boolean);

    return (
        <div
            className="min-h-screen atlas-hero-bg"
            style={{
                background:
                    "radial-gradient(ellipse 80% 50% at 50% -10%, rgba(139, 92, 246, 0.14) 0%, transparent 60%), " +
                    "radial-gradient(ellipse 60% 40% at 90% 20%, rgba(6, 182, 212, 0.08) 0%, transparent 55%), " +
                    "linear-gradient(180deg, #0f0f24 0%, #0a0a1a 55%, #050510 100%)",
                color: "var(--atlas-text-primary)",
            }}
        >
            {/* ============= 1. Sticky Nav ============= */}
            <Nav onDemo={() => openDemo("General")} />

            {/* ============= 2. Hero ============= */}
            <section className="max-w-6xl mx-auto px-6 pt-16 pb-16 lg:pb-24 grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-16 items-center">
                <div>
                    <div
                        className="inline-flex items-center gap-2 rounded-full px-3 h-7 text-[11px] font-medium"
                        style={{
                            background: "rgba(139,92,246,0.10)",
                            border: "1px solid rgba(139,92,246,0.35)",
                            color: "var(--atlas-primary-hover)",
                        }}
                        data-testid="hero-eyebrow"
                    >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--atlas-primary)", boxShadow: "0 0 8px var(--atlas-primary-glow)" }} />
                        40 Workforce Members. 600+ Integrations. One workspace.
                    </div>
                    {/* Sprint Q-1 · language reach marketing chip */}
                    <div
                        className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 rounded-full text-[12px]"
                        style={{
                            background: "rgba(139,92,246,0.10)",
                            border: "1px solid rgba(139,92,246,0.35)",
                            color: "var(--atlas-primary-hover)",
                        }}
                        data-testid="lang-100-marketing-chip"
                    >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--atlas-primary)", boxShadow: "0 0 8px var(--atlas-primary-glow)" }} />
                        Workforce Members speak 100+ languages
                    </div>
                    <h1
                        data-testid="hero-h1"
                        className="atlas-text-gradient mt-5 text-[46px] sm:text-[56px] lg:text-[68px] font-bold tracking-[-0.03em] leading-[1.02]"
                    >
                        The AI Workforce<br />Operating System
                    </h1>
                    <h2
                        data-testid="hero-h2"
                        className="mt-5 text-[20px] sm:text-[22px] font-medium tracking-tight max-w-[540px]"
                        style={{ color: "var(--atlas-text-secondary)" }}
                    >
                        A team of specialists, each living in the tools you already use.
                    </h2>
                    <p className="mt-4 text-[15px] leading-relaxed max-w-[540px]" style={{ color: "var(--atlas-text-secondary)" }}>
                        Your sales specialist lives in your CRM. Your data analyst lives in your analytics. Your legal assistant lives in your contract repo. One click and they're operational.
                    </p>
                    <div className="mt-7 flex flex-wrap items-center gap-3">
                        <Link
                            to="/signup"
                            data-testid="hero-cta-start"
                            className="atlas-btn-primary inline-flex items-center gap-1.5 rounded-lg px-5 h-12 text-[15px]"
                        >
                            Start free <ArrowRight className="w-4 h-4" strokeWidth={2} />
                        </Link>
                        <button
                            onClick={() => openDemo("General")}
                            data-testid="hero-cta-demo"
                            className="atlas-btn-ghost inline-flex items-center gap-1.5 rounded-lg px-5 h-12 text-[15px] font-semibold"
                        >
                            Book a demo
                        </button>
                    </div>
                    <div className="mt-4 text-[11.5px] text-gray-500">
                        No credit card · SOC 2 in progress · Multi-tenant secure
                    </div>
                </div>

                {/* Product frame — hero reel */}
                <div className="lg:pl-4" style={{ transform: "rotate(1.5deg)" }}>
                    <div className="relative">
                        <div className="absolute inset-0 -m-8 bg-gradient-to-tr from-[#16a34a]/12 via-[#0ea5e9]/10 to-transparent rounded-3xl blur-3xl" />
                        <div className="relative" style={{ transform: "rotate(-1.5deg)" }}>
                            <HeroReel workers={functional.length > 0 ? functional : workers} />
                        </div>
                    </div>
                </div>
            </section>

            {/* ============= 3. Social-proof band (Sprint E) ============= */}
            <SocialProofBand workers={functional.length > 0 ? functional : workers} />

            {/* ============= 4. Meets your stack — logo strip ============= */}
            <section className="border-y border-[#e8e8e3] bg-white">
                <div className="max-w-6xl mx-auto px-6 py-12 text-center">
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">
                        Native connections across your stack
                    </div>
                    <div
                        className="mt-6 flex flex-wrap justify-center items-center gap-x-5 gap-y-3"
                        data-testid="stack-logos"
                    >
                        {STACK_LOGOS.map((s) => (
                            <span key={s.slug} className="inline-flex items-center gap-1.5 text-gray-600">
                                <IntegrationLogo integration={s} size={22} rounded="rounded-md" />
                                <span className="text-[12.5px] font-medium">{s.name}</span>
                            </span>
                        ))}
                    </div>
                    <div className="mt-6 text-[13.5px] text-gray-700" data-testid="stack-integrations-count">
                        <span className="font-bold text-gray-900">600+ integrations.</span>{" "}
                        Meet every tool your team already uses.{" "}
                        <Link
                            to="/integrations"
                            className="text-[#16a34a] font-semibold hover:text-[#15803d] inline-flex items-center gap-1"
                            data-testid="stack-browse-catalog"
                        >
                            Browse the catalog <ArrowRight className="w-3 h-3" strokeWidth={2} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* ============= 4. Workforce Members vs Generic AI Chat Windows ============= */}
            <div id="compare"><ComparisonTable /></div>

            {/* ============= 4b. Character section (Sprint D addendum #2) ============= */}
            <CharacterSection />

            {/* ============= 5. Value pillars ============= */}
            <section className="max-w-6xl mx-auto px-6 py-24">
                <div className="text-center max-w-2xl mx-auto">
                    <div className="wfos-label text-gray-500">Why Nexus One</div>
                    <h2 className="atlas-text-gradient mt-3 text-4xl sm:text-5xl font-bold tracking-tight">
                        Not another prompt window. A digital workforce.
                    </h2>
                </div>
                <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="value-pillars">
                    {VALUE_PILLARS.map(({ icon: Icon, title, body }) => (
                        <div key={title} className="wfos-card p-7">
                            <div className="w-11 h-11 rounded-xl bg-[#dcfce7] grid place-items-center">
                                <Icon className="w-5 h-5 text-[#16a34a]" strokeWidth={1.75} />
                            </div>
                            <div className="mt-5 text-lg font-semibold text-gray-900">{title}</div>
                            <p className="mt-2 text-[14.5px] text-gray-600 leading-relaxed">{body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ============= 6. Marketplace preview strip ============= */}
            <section className="max-w-6xl mx-auto px-6 pb-20">
                <div className="flex items-end justify-between mb-6">
                    <div>
                        <div className="wfos-label text-gray-500">The team</div>
                        <h2 className="atlas-text-gradient mt-2 text-3xl font-bold tracking-tight">
                            Six specialists ready today.
                        </h2>
                    </div>
                    <Link to="/signup" className="text-sm font-medium text-[#16a34a] hover:text-[#15803d] inline-flex items-center gap-1">
                        Meet all 22 <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                    </Link>
                </div>
                <div className="flex overflow-x-auto snap-x snap-mandatory gap-4 pb-4 wfos-scrollbar" data-testid="marketplace-preview">
                    {functional.map((w) => (
                        <a
                            key={w.slug}
                            href={`#meet-${w.slug}`}
                            data-testid={`preview-card-${w.slug}`}
                            className="snap-start flex-shrink-0 w-[260px] wfos-card p-4 hover:border-[#d6d6cf] transition-colors"
                        >
                            <img src={resolve(w.avatar_url)} alt={w.name} className="w-16 h-16 rounded-full object-cover border border-[#e8e8e3]" />
                            <div className="mt-3 text-[15px] font-semibold text-gray-900">{w.name}</div>
                            <div className="text-[11px] uppercase tracking-widest text-gray-500 mt-0.5">{w.role}</div>
                            <div className="mt-1 text-[11.5px] text-gray-500 line-clamp-2">{w.specialization}</div>
                            <TraitDots traits={w.character_traits} size="xs" className="mt-2" />
                            <div className="mt-3 text-[11px] font-medium text-[#16a34a]">Meet {w.name} →</div>
                        </a>
                    ))}
                </div>
            </section>

            {/* ============= 6b. Meet your team — 6 alternating full-width ============= */}
            <section className="border-t border-[#e8e8e3] bg-white">
                <div className="max-w-6xl mx-auto px-6 py-24">
                    <div className="text-center mb-12">
                        <div className="wfos-label text-gray-500">Meet a few of your future Workforce Members</div>
                        <h2 className="atlas-text-gradient mt-3 text-4xl sm:text-5xl font-bold tracking-tight">
                            Your future team.
                        </h2>
                    </div>
                    <div className="space-y-24">
                        {functional.map((w, i) => (
                            <MeetSection key={w.slug} worker={w} sampleTask={SAMPLE_TASKS[w.slug]} reverse={i % 2 === 1} />
                        ))}
                    </div>
                </div>
            </section>

            {/* ============= 7. Sectors strip ============= */}
            <section className="border-t border-[#e8e8e3] bg-[#fafaf7]">
                <div className="max-w-6xl mx-auto px-6 py-24">
                    <div className="text-center mb-10">
                        <div className="wfos-label text-gray-500">Sectors</div>
                        <h2 className="atlas-text-gradient mt-3 text-3xl font-bold tracking-tight">
                            Verticalized for the shape of your work.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="solutions-grid">
                        {SOLUTIONS.map((s) => (
                            <div key={s.name} className="wfos-card p-5">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="text-[15px] font-semibold text-gray-900">{s.name}</div>
                                    <span className={`wfos-pill text-[10px] ${s.pill.startsWith("Available") ? "wfos-pill--green" : ""}`}>
                                        {s.pill}
                                    </span>
                                </div>
                                <p className="text-[13.5px] text-gray-600 leading-relaxed">{s.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============= 7b. Integration Marketplace teaser (Sprint H) ============= */}
            <section className="border-t border-[#e8e8e3] bg-white">
                <div className="max-w-6xl mx-auto px-6 py-24" data-testid="marketplace-teaser">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                        <div>
                            <div className="wfos-label text-[#16a34a]">Integration Marketplace</div>
                            <div className="mt-3 flex items-baseline gap-3">
                                <div className="text-6xl sm:text-7xl font-bold text-gray-900 tabular-nums" data-testid="marketplace-teaser-count">600+</div>
                                <div className="text-lg text-gray-600 font-medium">integrations across every category</div>
                            </div>
                            <p className="mt-5 text-[15px] text-gray-600 leading-relaxed max-w-md">
                                Your Workforce Members meet your team where they already work — CRM, ERP, comms, docs, dev, marketing, finance, HR, legal, and more.
                            </p>
                            <div className="mt-6">
                                <Link
                                    to="/integrations"
                                    data-testid="marketplace-teaser-cta"
                                    className="inline-flex items-center gap-1.5 rounded-lg bg-[#111827] px-4 h-11 text-sm font-semibold text-white hover:bg-gray-800"
                                >
                                    Browse integration catalog <ArrowRight className="w-4 h-4" strokeWidth={2} />
                                </Link>
                            </div>
                        </div>
                        <div className="grid grid-cols-4 sm:grid-cols-5 gap-2.5">
                            {STACK_LOGOS.slice(0, 20).map((s) => (
                                <div
                                    key={s.slug}
                                    className="wfos-card p-3 flex flex-col items-center gap-1.5 hover:border-[#d6d6cf] transition-colors"
                                    data-testid={`teaser-logo-${s.slug}`}
                                >
                                    <IntegrationLogo integration={s} size={30} rounded="rounded-lg" />
                                    <div className="text-[10px] text-gray-500 truncate max-w-full">{s.name.split(" ")[0]}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ============= 8. Pricing preview ============= */}
            <section className="max-w-6xl mx-auto px-6 py-24">
                <div className="text-center max-w-2xl mx-auto">
                    <div className="wfos-label text-gray-500">Pricing</div>
                    <h2 className="atlas-text-gradient mt-3 text-4xl sm:text-5xl font-bold tracking-tight">
                        Simple pricing. <span className="text-[#16a34a]">Serious workforce.</span>
                    </h2>
                    <p className="mt-3 text-[15px] text-gray-500">
                        Pick a workspace plan for baseline capacity, then activate specialists à la carte.
                    </p>
                </div>
                <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="landing-pricing-preview">
                    {[
                        { name: "Starter",       price: 299,  cap: "5 employees",  best: "Solo & small teams" },
                        { name: "Professional",  price: 799,  cap: "15 employees", best: "Departments", popular: true },
                        { name: "Business",      price: 1999, cap: "30 employees", best: "Mid-market" },
                        { name: "Enterprise",    price: 4999, cap: "75 employees", best: "Multi-department" },
                    ].map((p) => (
                        <div key={p.name} className={`wfos-card p-5 relative ${p.popular ? "border-[#16a34a] ring-1 ring-[#16a34a]/30" : ""}`}>
                            {p.popular && (
                                <span className="absolute -top-2 left-1/2 -translate-x-1/2 wfos-pill wfos-pill--green text-[10px]">
                                    Most popular
                                </span>
                            )}
                            <div className="text-[13px] font-semibold text-gray-900">{p.name}</div>
                            <div className="mt-3 text-3xl font-bold text-gray-900 tabular-nums">${p.price}<span className="text-sm font-normal text-gray-500">/mo</span></div>
                            <div className="mt-1 text-[11px] font-medium text-[#16a34a]">{p.cap}</div>
                            <div className="mt-1 text-[11px] text-gray-500">{p.best}</div>
                        </div>
                    ))}
                </div>
                <div className="mt-8 text-center">
                    <Link
                        to="/pricing"
                        data-testid="landing-see-pricing"
                        className="inline-flex items-center gap-1 text-sm font-medium text-[#16a34a] hover:text-[#15803d]"
                    >
                        See full pricing — plans, à la carte hires, skill packs <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                    </Link>
                </div>
            </section>

            {/* ============= 9. How it works ============= */}
            <section className="border-y border-[#e8e8e3] bg-white">
                <div className="max-w-6xl mx-auto px-6 py-24">
                    <div className="text-center mb-14">
                        <div className="wfos-label text-gray-500">How it works</div>
                        <h2 className="mt-3 text-4xl font-bold tracking-tight text-gray-900">
                            From zero to your first specialist in 60 seconds.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {STEPS.map(({ n, title, body, Icon }) => (
                            <div key={n} className="wfos-card p-6">
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-full bg-[#111827] grid place-items-center text-white text-sm font-semibold">{n}</div>
                                    <Icon className="w-5 h-5 text-[#16a34a]" strokeWidth={1.75} />
                                </div>
                                <div className="mt-4 text-lg font-semibold text-gray-900">{title}</div>
                                <p className="mt-1 text-[14px] text-gray-600 leading-relaxed">{body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============= 10. Security & compliance ============= */}
            <section className="max-w-6xl mx-auto px-6 py-24 text-center">
                <div className="wfos-label text-gray-500">Security & compliance</div>
                <h2 className="atlas-text-gradient mt-3 text-3xl font-bold tracking-tight">
                    Serious governance from day one.
                </h2>
                <div className="mt-8 flex flex-wrap justify-center gap-2" data-testid="security-badges">
                    {SECURITY_BADGES.map((b) => (
                        <span key={b.label} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#e8e8e3] px-3 h-7 text-[11.5px] font-medium">
                            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: b.color }} />
                            <span className="text-gray-800">{b.label}</span>
                        </span>
                    ))}
                </div>
                <p className="mt-6 text-xs text-gray-500 max-w-lg mx-auto">
                    We don't display badges for certifications we haven't earned. "In progress" means active audit, not marketing copy.
                </p>
            </section>

            {/* ============= 11. Testimonials ============= */}
            <section className="border-y border-[#e8e8e3] bg-[#fafaf7]">
                <div className="max-w-6xl mx-auto px-6 py-24">
                    <div className="text-center mb-10">
                        <div className="wfos-label text-gray-500">Illustrative — early-access customers</div>
                        <h2 className="atlas-text-gradient mt-3 text-3xl font-bold tracking-tight">
                            What early teams are saying.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="testimonials">
                        {TESTIMONIALS.map((t, i) => (
                            <figure key={i} data-testid={`testimonial-${i}`} className="wfos-card p-5">
                                <div className="flex gap-0.5 mb-3">
                                    {Array.from({ length: 5 }).map((_, k) => (
                                        <Star key={k} className="w-3 h-3 text-amber-500 fill-amber-500" strokeWidth={1} />
                                    ))}
                                </div>
                                <blockquote className="text-[14px] text-gray-800 leading-relaxed">"{t.quote}"</blockquote>
                                <figcaption className="mt-4 text-[11.5px] text-gray-500">— {t.who}, {t.type}</figcaption>
                            </figure>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============= 12. FAQ ============= */}
            <section className="max-w-4xl mx-auto px-6 py-24">
                <div className="text-center mb-10">
                    <div className="wfos-label text-gray-500">FAQ</div>
                    <h2 className="atlas-text-gradient mt-3 text-3xl font-bold tracking-tight">
                        Questions? Answers.
                    </h2>
                </div>
                <div className="space-y-2" data-testid="faq-list">
                    {FAQS.map(([q, a], i) => (
                        <details key={i} className="wfos-card p-4 group" data-testid={`faq-${i}`}>
                            <summary className="cursor-pointer list-none flex items-center justify-between gap-3">
                                <span className="text-[15px] font-medium text-gray-900">{q}</span>
                                <span className="w-6 h-6 rounded-full border border-[#e8e8e3] grid place-items-center text-gray-400 group-open:rotate-45 transition-transform">+</span>
                            </summary>
                            <p className="mt-3 text-[14px] text-gray-600 leading-relaxed">{a}</p>
                        </details>
                    ))}
                </div>
            </section>

            {/* ============= 13. Final CTA band ============= */}
            <section className="relative overflow-hidden border-t" style={{ borderColor: "rgba(139,92,246,0.16)" }} data-testid="final-cta">
                <div
                    className="absolute inset-0 opacity-70"
                    style={{
                        backgroundImage: "radial-gradient(circle at 20% 20%, rgba(139,92,246,0.28) 0%, transparent 45%), radial-gradient(circle at 80% 80%, rgba(6,182,212,0.20) 0%, transparent 45%)",
                    }}
                />
                <div className="relative max-w-4xl mx-auto px-6 py-24 text-center">
                    <h2 className="atlas-text-gradient text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
                        Meet your Digital Workforce today.
                    </h2>
                    <p className="mt-4 text-lg max-w-xl mx-auto" style={{ color: "var(--atlas-text-secondary)" }}>
                        40 Workforce Members · 600+ integrations · one workspace.
                    </p>
                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        <Link
                            to="/signup"
                            className="atlas-btn-primary inline-flex items-center gap-1.5 rounded-lg px-5 h-12 text-[15px]"
                        >
                            Start free <ArrowRight className="w-4 h-4" strokeWidth={2} />
                        </Link>
                        <button
                            onClick={() => openDemo("General")}
                            className="atlas-btn-ghost inline-flex items-center gap-1.5 rounded-lg px-5 h-12 text-[15px] font-semibold"
                        >
                            Book a demo
                        </button>
                    </div>
                </div>
            </section>

            {/* ============= 14. Footer ============= */}
            <Footer />

            <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} defaultInterest={demoInterest} />
        </div>
    );
}

/* ------------------- Sub-components ------------------- */

function resolve(url) {
    if (!url) return "https://images.unsplash.com/photo-1500648767791-00dcc994a43e";
    if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return url;
}

function Nav({ onDemo }) {
    const linkBase =
        "relative px-1 py-1 text-[13.5px] font-medium transition-colors " +
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--atlas-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a1a] rounded";
    const linkStyle = { color: "var(--atlas-text-primary)" };
    const onHoverIn = (e) => (e.currentTarget.style.color = "var(--atlas-primary-hover)");
    const onHoverOut = (e) => (e.currentTarget.style.color = "var(--atlas-text-primary)");
    return (
        <header
            className="sticky top-0 z-40 backdrop-blur-xl"
            style={{
                background:
                    "linear-gradient(180deg, rgba(10, 10, 26, 0.82) 0%, rgba(10, 10, 26, 0.72) 100%)",
                borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
                boxShadow: "0 4px 24px -12px rgba(0, 0, 0, 0.6)",
            }}
            data-testid="landing-nav"
        >
            <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
                <Link to="/" className="inline-flex items-center gap-2">
                    <div
                        className="atlas-logo-mark w-8 h-8 text-[13px]"
                        style={{
                            background: "linear-gradient(135deg, #a78bfa 0%, #7c3aed 60%, #4c1d95 100%)",
                            color: "#ffffff",
                            border: "1px solid rgba(167, 139, 250, 0.55)",
                            boxShadow: "0 0 12px -2px rgba(167, 139, 250, 0.55)",
                        }}
                    >
                        N
                    </div>
                    <span
                        className="text-[16px] font-semibold tracking-tight"
                        style={{ color: "var(--atlas-text-primary)" }}
                    >
                        Nexus One
                    </span>
                </Link>
                <nav className="hidden md:flex items-center gap-6">
                    <Link to="/" className={linkBase} style={linkStyle}
                        onMouseEnter={onHoverIn} onMouseLeave={onHoverOut} data-testid="nav-product">Product</Link>
                    <Link to="/pricing" className={linkBase} style={linkStyle}
                        onMouseEnter={onHoverIn} onMouseLeave={onHoverOut} data-testid="nav-pricing">Pricing</Link>
                    <SectorsMegaMenu />
                    <Link to="/#testimonials" className={linkBase} style={linkStyle}
                        onMouseEnter={onHoverIn} onMouseLeave={onHoverOut} data-testid="nav-customers">Customers</Link>
                    <a href="#docs" onClick={(e) => e.preventDefault()} className={linkBase} style={linkStyle}
                        onMouseEnter={onHoverIn} onMouseLeave={onHoverOut} data-testid="nav-docs">Docs</a>
                    <button onClick={onDemo} className={linkBase} style={linkStyle}
                        onMouseEnter={onHoverIn} onMouseLeave={onHoverOut} data-testid="nav-book-demo">Book demo</button>
                </nav>
                <div className="flex items-center gap-3">
                    <Link
                        to="/login"
                        data-testid="nav-signin"
                        className="inline-flex items-center rounded-lg px-3 h-9 text-[13px] font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--atlas-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a1a]"
                        style={{
                            color: "var(--atlas-text-primary)",
                            border: "1px solid rgba(255, 255, 255, 0.18)",
                            background: "rgba(255, 255, 255, 0.04)",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = "rgba(167, 139, 250, 0.65)";
                            e.currentTarget.style.background = "rgba(139, 92, 246, 0.14)";
                            e.currentTarget.style.color = "#ffffff";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.18)";
                            e.currentTarget.style.background = "rgba(255, 255, 255, 0.04)";
                            e.currentTarget.style.color = "var(--atlas-text-primary)";
                        }}
                    >
                        Sign in
                    </Link>
                    <Link
                        to="/signup"
                        data-testid="nav-start-free"
                        className="atlas-btn-primary inline-flex items-center gap-1 rounded-lg px-3.5 h-9 text-[13px] font-semibold"
                    >
                        Start free
                    </Link>
                </div>
            </div>
        </header>
    );
}

function MeetSection({ worker, sampleTask, reverse }) {
    return (
        <div
            id={`meet-${worker.slug}`}
            data-testid={`meet-${worker.slug}`}
            className={`grid grid-cols-1 md:grid-cols-2 gap-10 items-center ${reverse ? "md:[direction:rtl]" : ""}`}
        >
            <div className={reverse ? "md:[direction:ltr]" : ""}>
                <img
                    src={resolve(worker.avatar_url)}
                    alt={worker.name}
                    className="w-full max-w-sm aspect-[3/4] rounded-2xl object-cover border border-[#e8e8e3] shadow-md"
                />
            </div>
            <div className={reverse ? "md:[direction:ltr]" : ""}>
                <div className="wfos-label text-gray-500">{worker.role}</div>
                <h3 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
                    {worker.name}
                </h3>
                <TraitDots traits={worker.character_traits} size="sm" showLabels className="mt-3" />
                <p className="mt-4 text-[15px] text-gray-600 leading-relaxed max-w-lg">
                    {worker.bio}
                </p>
                {(worker.career_highlights || []).length > 0 && (
                    <ul className="mt-4 space-y-1.5 text-[13.5px] text-gray-700 max-w-lg">
                        {(worker.career_highlights || []).slice(0, 3).map((h, i) => (
                            <li key={i} className="flex items-start gap-2">
                                <Check className="w-3.5 h-3.5 text-[#16a34a] mt-0.5 flex-shrink-0" strokeWidth={2} />
                                <span>{h}</span>
                            </li>
                        ))}
                    </ul>
                )}
                {sampleTask && (
                    <div className="mt-6 rounded-2xl border border-[#e8e8e3] bg-[#fbfef9] p-4 max-w-lg">
                        <div className="text-[10px] uppercase tracking-widest text-[#16a34a] font-semibold mb-1.5">
                            Sample handoff
                        </div>
                        <blockquote className="text-[14px] text-gray-800 italic leading-relaxed">
                            "{sampleTask}"
                        </blockquote>
                    </div>
                )}
                {(worker.recommended_integrations || []).length > 0 && (
                    <div className="mt-5">
                        <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-2">
                            Lives in
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {(worker.recommended_integrations || []).slice(0, 6).map((iid) => (
                                <span key={iid} className="wfos-pill text-[10px]">
                                    {iid.replace(/-/g, " ")}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
                <div className="mt-6">
                    <Link
                        to="/signup"
                        data-testid={`hire-${worker.slug}`}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-[#16a34a] px-4 h-11 text-sm font-semibold text-white hover:bg-[#15803d]"
                    >
                        Activate {worker.name} — ${worker.monthly_price?.toLocaleString() || "—"}/mo <ArrowRight className="w-4 h-4" strokeWidth={2} />
                    </Link>
                </div>
            </div>
        </div>
    );
}

function Footer() {
    return (
        <footer className="border-t border-[#e8e8e3] bg-white">
            <div className="max-w-6xl mx-auto px-6 py-12 grid grid-cols-2 md:grid-cols-5 gap-6">
                <div className="col-span-2 md:col-span-1">
                    <div className="flex items-center gap-2">
                        <div className="atlas-logo-mark w-7 h-7 text-[11px]">N</div>
                        <span className="text-[15px] font-semibold text-gray-900">Nexus One</span>
                    </div>
                    <p className="mt-3 text-[12.5px] text-gray-500 leading-relaxed max-w-[220px]">
                        The AI Workforce Operating System. Activate, manage, and scale your digital workforce.
                    </p>
                </div>
                {[
                    ["Product",   [["Marketplace", "/signup"], ["Pricing", "/pricing"], ["Skill packs", "/pricing"]]],
                    ["Solutions", [["Legal", "/sectors/legal"], ["Sales", "/sectors/sales"], ["Consulting", "/sectors/consulting"]]],
                    ["Company",   [["About", "#"], ["Careers", "#"], ["Contact", "#"]]],
                    ["Legal",     [["Privacy", "/privacy"], ["Terms", "/terms"], ["DPA", "/legal/dpa"], ["Security", "#"]]],
                ].map(([title, items]) => (
                    <div key={title}>
                        <div className="text-[11px] font-semibold uppercase tracking-widest text-gray-500">{title}</div>
                        <ul className="mt-3 space-y-2">
                            {items.map(([label, to]) => (
                                <li key={label}>
                                    <Link to={to} className="text-[13px] text-gray-700 hover:text-gray-900">{label}</Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
            <div className="border-t border-[#e8e8e3] max-w-6xl mx-auto px-6 py-6 text-[11.5px] text-gray-500 flex flex-col sm:flex-row justify-between gap-2">
                <span>© {new Date().getFullYear()} Nexus One. All rights reserved.</span>
                <span>Multi-tenant secure · SOC 2 in progress</span>
            </div>
        </footer>
    );
}
